import { parseChatbotBody } from '../models/chatbotBody'
import type {
  FeatureAskRequestDto,
  FeatureAskResponseDto,
  FeatureCatalogResponseDto,
  FeatureChatHistoryResponseDto,
  FeatureSetupSessionResponseDto,
} from '../models/featureChat.dto'
import type {
  BookmarkResultDto,
  BulkDeleteResultDto,
  ChatRequestDto,
  ChatResponseDto,
  DeleteThreadResultDto,
  FeedbackRequestDto,
  FeedbackResultDto,
  RecommendationDecisionRequestDto,
  RenameResultDto,
  ThreadHistoryDto,
  ThreadListDto,
} from '../models/orpheus.dto'
import type { WorkspaceId } from '../models/orpheus.types'
import type { DraftConfirmResultDto } from '../models/orpheusSetup.types'

import { appHttp, decodeDto } from '@/shared/http'

type OrpheusHttpClient = Pick<typeof appHttp, 'delete' | 'get' | 'patch' | 'post'>

export interface OrpheusRequestOptions {
  signal?: AbortSignal
}

export interface OrpheusChatRequestOptions extends OrpheusRequestOptions {
  /** Owned by the logical turn; callers must reuse it for retries/resume. */
  idempotencyKey: string
}

const FEATURE_OWNED = {
  suppressGlobalErrorHandler: true,
  meta: { suppressGlobalErrorHandler: true },
} as const

/**
 * A chat turn that reads an attached KPI file runs past two minutes on the backend (ORP-27305),
 * and cutting it here doesn't stop it there: the answer is still stored and billed, and a retry
 * asks twice. v3's MCP timeout, kept.
 */
export const CHAT_TURN_TIMEOUT_MS = 240_000

function scoped(workspaceId: WorkspaceId): string {
  return `/api/chatbot/workspaces/${encodeURIComponent(String(workspaceId))}`
}

function threadSegment(threadId: string): string {
  return encodeURIComponent(threadId)
}

/**
 * Feature chat rides the SAME canonical base as the rest of this class (ORP-20465).
 *
 * The legacy `/accounts/<id>/copilot/workspaces/<ws>/feature-chat/*` alias stays mounted for v3
 * alone; the moment v4 calls it, the backend can never retire it. `scoped()` above carries no
 * `account_id` and none is added here.
 *
 * These three paths carry NO trailing slash — matching `orpheus/urls.py`, which registers them
 * bare while the thread/chat routes above are registered with one.
 */
function featureChat(workspaceId: WorkspaceId): string {
  return `${scoped(workspaceId)}/feature-chat`
}

function requestConfig(
  options?: OrpheusRequestOptions,
): typeof FEATURE_OWNED & { signal?: AbortSignal } {
  return { ...FEATURE_OWNED, ...(options?.signal ? { signal: options.signal } : {}) }
}

function decodeFlatDto<T>(body: unknown): T {
  return decodeDto(body, (value) => {
    const decoded = parseChatbotBody<unknown>(value)
    if (!decoded || typeof decoded !== 'object' || Array.isArray(decoded)) {
      throw new Error('Malformed chatbot response')
    }
    return decoded as T
  })
}

function decodeOptionalFlatDto<T>(body: unknown): T | null {
  return body === undefined || body === null || body === '' ? null : decodeFlatDto<T>(body)
}

export class OrpheusApi {
  constructor(private readonly http: OrpheusHttpClient = appHttp) {}

  async postChat(
    workspaceId: WorkspaceId,
    body: ChatRequestDto,
    options: OrpheusChatRequestOptions,
  ): Promise<ChatResponseDto> {
    const response = await this.http.post<unknown>(`${scoped(workspaceId)}/chat/`, body, {
      ...requestConfig(options),
      meta: { ...FEATURE_OWNED.meta, timeoutMs: CHAT_TURN_TIMEOUT_MS },
      headers: { 'Idempotency-Key': options.idempotencyKey },
    })
    return decodeFlatDto<ChatResponseDto>(response.data)
  }

  async postRecommendationDecision(
    workspaceId: WorkspaceId,
    body: RecommendationDecisionRequestDto,
    options: OrpheusChatRequestOptions,
  ): Promise<ChatResponseDto> {
    const response = await this.http.post<unknown>(
      `${scoped(workspaceId)}/chat/recommendations/decision/`,
      body,
      {
        ...requestConfig(options),
        headers: { 'Idempotency-Key': options.idempotencyKey },
      },
    )
    return decodeFlatDto<ChatResponseDto>(response.data)
  }

  async listThreads(
    workspaceId: WorkspaceId,
    before?: string | null,
    options?: OrpheusRequestOptions,
  ): Promise<ThreadListDto> {
    const response = await this.http.get<unknown>(`${scoped(workspaceId)}/v2/threads/`, {
      ...requestConfig(options),
      ...(before ? { params: { before } } : {}),
    })
    return decodeFlatDto<ThreadListDto>(response.data)
  }

  async getThreadHistory(
    workspaceId: WorkspaceId,
    threadId: string,
    options?: OrpheusRequestOptions,
  ): Promise<ThreadHistoryDto> {
    const response = await this.http.get<unknown>(
      `${scoped(workspaceId)}/threads/${threadSegment(threadId)}/history/`,
      requestConfig(options),
    )
    return decodeFlatDto<ThreadHistoryDto>(response.data)
  }

  async renameThread(
    workspaceId: WorkspaceId,
    threadId: string,
    title: string,
    options?: OrpheusRequestOptions,
  ): Promise<RenameResultDto> {
    const response = await this.http.patch<unknown>(
      `${scoped(workspaceId)}/v2/threads/${threadSegment(threadId)}/title/`,
      { title },
      requestConfig(options),
    )
    return decodeFlatDto<RenameResultDto>(response.data)
  }

  async setThreadBookmark(
    workspaceId: WorkspaceId,
    threadId: string,
    bookmarked: boolean,
    options?: OrpheusRequestOptions,
  ): Promise<BookmarkResultDto> {
    const response = await this.http.post<unknown>(
      `${scoped(workspaceId)}/v2/threads/${threadSegment(threadId)}/bookmark/`,
      { bookmarked },
      requestConfig(options),
    )
    return decodeFlatDto<BookmarkResultDto>(response.data)
  }

  async deleteThread(
    workspaceId: WorkspaceId,
    threadId: string,
    options?: OrpheusRequestOptions,
  ): Promise<DeleteThreadResultDto | null> {
    const response = await this.http.delete<unknown>(
      `${scoped(workspaceId)}/threads/${threadSegment(threadId)}/`,
      requestConfig(options),
    )
    return decodeOptionalFlatDto<DeleteThreadResultDto>(response.data)
  }

  async bulkDeleteThreads(
    workspaceId: WorkspaceId,
    threadIds: string[],
    options?: OrpheusRequestOptions,
  ): Promise<BulkDeleteResultDto> {
    const response = await this.http.post<unknown>(
      `${scoped(workspaceId)}/v2/threads/bulk-delete/`,
      { thread_ids: threadIds },
      requestConfig(options),
    )
    return decodeFlatDto<BulkDeleteResultDto>(response.data)
  }

  async submitFeedback(
    workspaceId: WorkspaceId,
    body: FeedbackRequestDto,
    options?: OrpheusRequestOptions,
  ): Promise<FeedbackResultDto> {
    const response = await this.http.post<unknown>(
      `${scoped(workspaceId)}/feedback/`,
      body,
      requestConfig(options),
    )
    return decodeFlatDto<FeedbackResultDto>(response.data)
  }

  /** The registry-driven feature catalog: which `feature_key`s exist and what each accepts. */
  async getFeatureCatalog(
    workspaceId: WorkspaceId,
    options?: OrpheusRequestOptions,
  ): Promise<FeatureCatalogResponseDto> {
    const response = await this.http.get<unknown>(
      `${featureChat(workspaceId)}/features`,
      requestConfig(options),
    )
    return decodeFlatDto<FeatureCatalogResponseDto>(response.data)
  }

  /**
   * Ask about one record. Feature-owned errors: the 429 (`Retry-After`) and the 402
   * `CREDIT_DEPLETED` both have to reach the panel rather than the global toast, which
   * `requestConfig`'s `suppressGlobalErrorHandler` already guarantees.
   */
  async askFeatureChat(
    workspaceId: WorkspaceId,
    body: FeatureAskRequestDto,
    options?: OrpheusRequestOptions,
  ): Promise<FeatureAskResponseDto> {
    const response = await this.http.post<unknown>(
      `${featureChat(workspaceId)}/ask`,
      body,
      requestConfig(options),
    )
    return decodeFlatDto<FeatureAskResponseDto>(response.data)
  }

  /** Session transcript, oldest first. `limit` is clamped 1–200 by the backend serializer. */
  async getFeatureChatHistory(
    workspaceId: WorkspaceId,
    sessionId: string,
    limit?: number,
    options?: OrpheusRequestOptions,
  ): Promise<FeatureChatHistoryResponseDto> {
    const response = await this.http.get<unknown>(
      `${featureChat(workspaceId)}/sessions/${encodeURIComponent(sessionId)}/history`,
      {
        ...requestConfig(options),
        ...(limit === undefined ? {} : { params: { limit } }),
      },
    )
    return decodeFlatDto<FeatureChatHistoryResponseDto>(response.data)
  }

  /**
   * The visitor's half-built setup on a setup surface, with its history — the session a
   * setup-mode ask without a `session_id` would resume. Lets a reopened panel offer "continue" or
   * "start over" before any turn is charged.
   */
  async getFeatureSetupSession(
    workspaceId: WorkspaceId,
    featureKey: string,
    options?: OrpheusRequestOptions,
  ): Promise<FeatureSetupSessionResponseDto> {
    const response = await this.http.get<unknown>(`${featureChat(workspaceId)}/setup-session`, {
      ...requestConfig(options),
      params: { feature_key: featureKey },
    })
    return decodeFlatDto<FeatureSetupSessionResponseDto>(response.data)
  }

  /**
   * The confirm button under a setup card (KUR-1): the only door that saves what Orpheus
   * prepared. No body — it applies the draft exactly as previewed, at most once — and it serves
   * the main chat and the feature-chat panels alike.
   */
  async confirmDraft(
    workspaceId: WorkspaceId,
    draftRef: string,
    options?: OrpheusRequestOptions,
  ): Promise<DraftConfirmResultDto> {
    const response = await this.http.post<unknown>(
      `${scoped(workspaceId)}/chat/drafts/${encodeURIComponent(draftRef)}/confirm/`,
      {},
      requestConfig(options),
    )
    return decodeOptionalFlatDto<DraftConfirmResultDto>(response.data) ?? {}
  }
}

export const orpheusApi = new OrpheusApi()
